import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCalls, Call } from '../../crm-calls/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface RecentCallsProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const RecentCalls: React.FC<RecentCallsProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { calls, isLoading } = useCalls();
  const arr = Array.isArray(calls) ? calls : [];

  const items = React.useMemo(() => {
    return [...arr]
      .sort((a: Call, b: Call) => {
        const ta = new Date(a.clientCallDate || 0).getTime();
        const tb = new Date(b.clientCallDate || 0).getTime();
        return tb - ta;
      })
      .slice(0, effectiveLimit);
  }, [arr, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  return (
    <GenericListWidget<Call>
      title="Recent Calls"
      viewAllPath="/calls"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No recent calls"
      getItemKey={(c) => c.clientCallId}
      embedded={embedded}
      renderItem={(call) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/calls/${call.clientCallId}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' && navigate(`/calls/${call.clientCallId}`)
          }
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text}`}>
              {call.client?.clientName || `Call #${call.clientCallId}`}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {call.clientCallType?.call_type_desc && `${call.clientCallType.call_type_desc} · `}
              {call.clientCallDate
                ? new Date(call.clientCallDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                : ''}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default RecentCalls;
